import { showConfirmModal } from './confirmModal.js';

/**
 * settingsPanel.js — Adapted for RaceProX
 *
 * Edit-mode only panel. Simplified — no driversPanel references.
 * The panel binds to the same DOM elements as the source version.
 */

/** @typedef {import('./settings.js').HologramSettings} HologramSettings */

/**
 * @param {{
 *   settings: HologramSettings;
 *   onSettingsChange: (key: keyof HologramSettings, value: number | string | boolean) => void;
 *   onSvgFile: (file: File) => void;
 *   onResetSvg: () => void;
 *   onMapFile: (file: File) => void;
 *   onResetMap: () => void;
 *   onDisplayFontFile?: (file: File) => void | Promise<void>;
 *   onResetDisplayFont?: () => void;
 *   onClearCustomCorners: () => void;
 *   onTogglePlaceCorners: (enabled: boolean) => void;
 *   onTogglePlaceStart?: (enabled: boolean) => void;
 *   onClearStartMarker?: () => void;
 *   onLapDirectionChange?: (clockwise: boolean) => void;
 *   onSaveSettings?: () => void | Promise<void>;
 *   onResetAllSettings?: () => void | Promise<void>;
 *   onExportSettings?: () => void | Promise<void>;
 *   onImportPresetFile?: (file: File) => void | Promise<void>;
 * }} options
 */
export function initSettingsPanel({
  settings,
  onSettingsChange,
  onSvgFile,
  onResetSvg,
  onMapFile,
  onResetMap,
  onDisplayFontFile,
  onResetDisplayFont,
  onClearCustomCorners,
  onTogglePlaceCorners,
  onTogglePlaceStart,
  onClearStartMarker,
  onLapDirectionChange,
  onSaveSettings,
  onResetAllSettings,
  onExportSettings,
  onImportPresetFile,
}) {
  const adminPanel = document.getElementById('admin-panel');
  if (!adminPanel) return;

  document.getElementById('settings-save')?.addEventListener('click', () => {
    onSaveSettings?.();
  });

  document.getElementById('settings-reset-all')?.addEventListener('click', () => {
    onResetAllSettings?.();
  });

  document.getElementById('settings-export')?.addEventListener('click', () => {
    onExportSettings?.();
  });

  const presetImportInput = /** @type {HTMLInputElement | null} */ (
    document.getElementById('preset-import')
  );
  document.getElementById('settings-import')?.addEventListener('click', () => {
    presetImportInput?.click();
  });
  presetImportInput?.addEventListener('change', () => {
    const file = presetImportInput.files?.[0];
    if (!file) return;
    onImportPresetFile?.(file);
    presetImportInput.value = '';
  });

  const fileInput = document.getElementById('svg-upload');
  const fileNameEl = document.getElementById('svg-file-name');
  const resetBtn = document.getElementById('svg-reset');

  fileInput?.addEventListener('change', () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    if (fileNameEl) fileNameEl.textContent = file.name;
    onSvgFile(file);
  });

  resetBtn?.addEventListener('click', () => {
    if (fileInput) fileInput.value = '';
    if (fileNameEl) fileNameEl.textContent = 'Default SVG';
    onResetSvg();
  });

  const mapInput = document.getElementById('map-upload');
  const mapFileNameEl = document.getElementById('map-file-name');
  const mapResetBtn = document.getElementById('map-reset');

  mapInput?.addEventListener('change', () => {
    const file = mapInput.files?.[0];
    if (!file) return;
    if (mapFileNameEl) mapFileNameEl.textContent = file.name;
    onMapFile(file);
  });

  mapResetBtn?.addEventListener('click', () => {
    if (mapInput) mapInput.value = '';
    if (mapFileNameEl) mapFileNameEl.textContent = 'None';
    onResetMap();
  });

  const displayFontInput = document.getElementById('display-font-upload');
  const displayFontNameEl = document.getElementById('display-font-file-name');
  const displayFontResetBtn = document.getElementById('display-font-reset');

  displayFontInput?.addEventListener('change', () => {
    const file = displayFontInput.files?.[0];
    if (!file) return;
    if (displayFontNameEl) displayFontNameEl.textContent = file.name;
    onDisplayFontFile?.(file);
  });

  displayFontResetBtn?.addEventListener('click', () => {
    if (displayFontInput) displayFontInput.value = '';
    if (displayFontNameEl) displayFontNameEl.textContent = 'Default (Inter)';
    onResetDisplayFont?.();
  });

  document.getElementById('start-place-mode')?.addEventListener('click', (e) => {
    const btn = /** @type {HTMLButtonElement} */ (e.currentTarget);
    const next = !btn.classList.contains('is-active');
    btn.classList.toggle('is-active', next);
    btn.textContent = next ? 'Click track to place…' : 'Place start line on track';
    onTogglePlaceStart?.(next);
  });

  document.getElementById('start-clear')?.addEventListener('click', () => {
    onClearStartMarker?.();
  });

  document.getElementById('lap-direction-cw')?.addEventListener('click', () => {
    syncLapDirectionButtons(true);
    onLapDirectionChange?.(true);
  });

  document.getElementById('lap-direction-ccw')?.addEventListener('click', () => {
    syncLapDirectionButtons(false);
    onLapDirectionChange?.(false);
  });

  document.getElementById('corner-place-mode')?.addEventListener('click', (e) => {
    const btn = /** @type {HTMLButtonElement} */ (e.currentTarget);
    const next = !btn.classList.contains('is-active');
    btn.classList.toggle('is-active', next);
    btn.textContent = next ? 'Click track to place…' : 'Place markers on track';
    onTogglePlaceCorners?.(next);
  });

  document.getElementById('corner-remove-all')?.addEventListener('click', async () => {
    const countEl = document.getElementById('markers-count');
    const count = parseInt(countEl?.textContent ?? '0', 10) || 0;
    const confirmed = await showConfirmModal({
      title: 'Remove all markers?',
      message:
        count === 1
          ? 'This will delete the marker on the circuit. This action cannot be undone.'
          : `This will delete all ${count} markers on the circuit. This action cannot be undone.`,
      confirmLabel: 'Remove all',
      cancelLabel: 'Cancel',
    });
    if (confirmed) onClearCustomCorners?.();
  });

  const trackNameTextInput = document.getElementById('track-name-text');
  trackNameTextInput?.addEventListener('input', () => {
    onSettingsChange('trackNameText', trackNameTextInput.value);
  });

  adminPanel.querySelectorAll('[data-setting]').forEach((el) => {
    const key = /** @type {keyof HologramSettings} */ (el.dataset.setting);
    const input = /** @type {HTMLInputElement} */ (el);

    const applyValue = () => {
      let value;
      if (input.type === 'checkbox') {
        value = input.checked;
      } else if (input.type === 'color') {
        value = input.value;
      } else {
        value = parseFloat(input.value);
        if (Number.isNaN(value)) return;
      }
      onSettingsChange(key, value);
    };

    input.addEventListener('input', applyValue);
    input.addEventListener('change', applyValue);
  });

  syncPanelFromSettings(settings);
}

export function syncRangeOutputs() {
  const adminPanel = document.getElementById('admin-panel');
  if (!adminPanel) return;
  adminPanel.querySelectorAll('input[type="range"][data-setting]').forEach((el) => {
    const key = el.dataset.setting;
    const output = adminPanel.querySelector(`output[data-for="${key}"]`);
    if (output) output.textContent = /** @type {HTMLInputElement} */ (el).value;
  });
}

export function resetPresetFileInputs() {
  const svgInput = /** @type {HTMLInputElement | null} */ (document.getElementById('svg-upload'));
  const mapInput = /** @type {HTMLInputElement | null} */ (document.getElementById('map-upload'));
  const displayFontInput = /** @type {HTMLInputElement | null} */ (
    document.getElementById('display-font-upload')
  );
  const presetImportInput = /** @type {HTMLInputElement | null} */ (
    document.getElementById('preset-import')
  );
  if (svgInput) svgInput.value = '';
  if (mapInput) mapInput.value = '';
  if (displayFontInput) displayFontInput.value = '';
  if (presetImportInput) presetImportInput.value = '';
}

export function resetPlaceCornerButton() {
  const btn = document.getElementById('corner-place-mode');
  if (!btn) return;
  btn.classList.remove('is-active');
  btn.textContent = 'Place markers on track';
}

export function resetPlaceStartButton() {
  const btn = document.getElementById('start-place-mode');
  if (!btn) return;
  btn.classList.remove('is-active');
  btn.textContent = 'Place start line on track';
}

/** @param {boolean} clockwise */
export function syncLapDirectionButtons(clockwise) {
  const cw = document.getElementById('lap-direction-cw');
  const ccw = document.getElementById('lap-direction-ccw');
  if (cw) cw.classList.toggle('is-active', clockwise);
  if (ccw) ccw.classList.toggle('is-active', !clockwise);
}

export function resetLapDirectionButtons() {
  syncLapDirectionButtons(true);
}

/**
 * @param {string} message
 * @param {'ok' | 'error' | ''} [tone]
 */
export function setPresetStatus(message, tone = '') {
  const el = document.getElementById('preset-status');
  if (!el) return;
  el.textContent = message;
  el.classList.toggle('error', tone === 'error');
}

/** @param {HologramSettings} settings */
export function syncPanelFromSettings(settings) {
  const trackNameTextInput = document.getElementById('track-name-text');
  if (trackNameTextInput && settings.trackNameText != null) {
    trackNameTextInput.value = settings.trackNameText;
  }

  const adminPanel = document.getElementById('admin-panel');
  if (adminPanel) {
    adminPanel.querySelectorAll('[data-setting]').forEach((el) => {
      const key = /** @type {keyof HologramSettings} */ (el.dataset.setting);
      const input = /** @type {HTMLInputElement} */ (el);
      const value = settings[key];

      if (input.type === 'checkbox') {
        input.checked = Boolean(value);
      } else {
        input.value = String(value);
      }
    });
  }

  syncRangeOutputs();
}
